/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import TextOnly from "../Components/StreamingComponents/TextOnly";
import BrandingCard from "../Components/StreamingComponents/BrandingCard";
import { useSelector, useDispatch } from "react-redux";
import {
  apiError,
  conversationList,
  isStream,
} from "../Services/Redux/Reducers/chatSlice";
import {
  uniqueSessionHistory,
  updateShopClicked
} from "../Services/ApiServices/chatServices";
import { onHistoryClick } from "../Services/Redux/Reducers/commonSlice";
import MultiCatTemplate from "../Components/MultiCatCardTemplate/MultiCatTemplate";
import { useLocation } from "react-router-dom";
import ChatOperation from "./ChatOperation";
import { ChatHistoryProps } from "../Services/CommonServices/propTypes";
import ProductListingGrid from "../Components/StreamingComponents/ProductLisitingGrid";
import SingleLookTemplate from "../Components/StreamingComponents/SingleLookTemplate";
import ProductDetailPage from "../Components/StreamingComponents/ProductDetailPage";
import { Cookies } from 'react-cookie-consent';
import { useLanguage } from "../Services/ContextProvider/LanguageContextProvider";

function ChatHistory(props) {
  const [data, setData] = useState([]);
  const [isScreenResponsive, setIsScreenResponsive] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const loading = useSelector((state) => state.common.isLoader);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const { sessionId } = (location?.state !== null && location?.state !== undefined) ? location.state : '';
  const customerId = Cookies?.get('ar_user_Id') ? Cookies.get('ar_user_Id') : "";
  const typewritterdelay = process.env.TYPE_WRITER_DELAY || 5;
  const [isMobile, setIsMobile] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [imageReady, setImageReady] = useState({});
  const initialLoadTimerRef = useRef({});
  const bottomRef = useRef(null);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [safariLoaderMinTime, setSafariLoaderMinTime] = useState(false);
  const [contentReadyToShow, setContentReadyToShow] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({
    isSafari: false,
    isIOS: false,
    isChrome: false
  });
  const { t } = useLanguage();

  const handleImageLoad = (conId, componentId) => {
    setLoadedImages((prev) => ({
      ...prev,
      [conId]: {
        ...(prev[conId] || {}),
        [componentId]: true,
      },
    }));
    setupInitialImageDelay(conId);
  };

  const isImageVisible = (conId, componentId) => {
    return loadedImages[conId]?.[componentId] === true && imageReady[conId] === true;
  };

  const setupInitialImageDelay = (conId) => {
    if (!initialLoadTimerRef.current[conId]) {
      initialLoadTimerRef.current[conId] = setTimeout(() => {
        setImageReady(prev => ({
          ...prev,
          [conId]: true
        }));
      }, 300);
    }
  };

  useEffect(() => {
    // Enhanced browser detection
    const userAgent = navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    
    setBrowserInfo({
      isSafari,
      isIOS,
      isChrome
    });

    // Reset the initial loader when navigating to chathistory
    if (pathname.includes('chathistory')) {
      setShowInitialLoader(true);
      setContentReadyToShow(false);
      setSafariLoaderMinTime(false);
      setIsContentLoaded(false);
      
      // For Safari and iOS, ensure minimum loader display time to prevent flashing
      if (isSafari || isIOS) {
        // Set a minimum loader time for Safari/iOS to prevent flash
        setTimeout(() => {
          setSafariLoaderMinTime(true);
        }, 1500); // Minimum 1.5 seconds for Safari/iOS
      } else {
        // For other browsers, shorter minimum time
        setTimeout(() => {
          setSafariLoaderMinTime(true);
        }, 800);
      }
    }
    storeMessage(sessionId);
    return () => {
      Object.values(initialLoadTimerRef.current).forEach(timer => clearTimeout(timer));
    };
  }, [pathname, sessionId]);

  const getBestImageUrl = (con, forDesktop = false) => {
    if (con.isSingleLook && con.response.outfitUrl) {
      return con.response.outfitUrl.split("?fmt=webp-alpha")[0] + "?$rl_4x5_pdp$&fmt=webp-alpha";
    }
    if (!forDesktop && con.backgroundImage) {
      return con.backgroundImage;
    }
    if (con.desktopbackgroundImage) {
      return con.desktopbackgroundImage;
    }
    return con.backgroundImage || "";
  };

  const storeMessage = (session) => {
    setHistoryLoading(true);
    setContentReadyToShow(false);
    dispatch(isStream(false));
    const params = [customerId, session];
    uniqueSessionHistory(
      params,
      (response) => {
        setData(response.data.data);
        dispatch(conversationList(response.data.data));
        // Mark history loading as complete, but don't show content yet
        setHistoryLoading(false);
      },
      (err) => {
        dispatch(apiError(err.message));
        setHistoryLoading(false);
        // Even on error, hide the loader after some time
        setTimeout(() => {
          if (safariLoaderMinTime) {
            setShowInitialLoader(false);
          }
        }, 500);
      }
    );
  };

  // Enhanced content loading detection
  useEffect(() => {
    if (data && data.length > 0 && !historyLoading) {
      // Check for conversations with images
      const allConversationsWithImages = data.filter(con => 
        con.backgroundImage || con.desktopbackgroundImage || 
        (con.response && con.response.outfitUrl)
      );
      
      if (allConversationsWithImages.length === 0) {
        // If there are no images to load, set content as loaded immediately
        setIsContentLoaded(true);
        setContentReadyToShow(true);
        return;
      }
      
      // Check if all conversation images are ready
      const allImagesLoaded = allConversationsWithImages.every(con => {
        const imagesLoadedForCon = loadedImages[con.uid];
        const imagesReadyForCon = imageReady[con.uid];
        
        if (!imagesLoadedForCon || !imagesReadyForCon) {
          return false;
        }
        
        // Check if all required image components are loaded
        const requiredComponents = isMobile ? ['mobileBgHeader'] : ['desktopBgHeader'];
        return requiredComponents.every(comp => imagesLoadedForCon[comp] === true);
      });
      
      if (allImagesLoaded) {
        // Additional delay for Safari/iOS to ensure DOM is fully ready
        const delay = browserInfo.isSafari || browserInfo.isIOS ? 700 : 300;
        setTimeout(() => {
          setIsContentLoaded(true);
          setContentReadyToShow(true);
        }, delay);
      }
    }
  }, [data, historyLoading, loadedImages, imageReady, isMobile, browserInfo]);

  // Enhanced scroll-to-bottom logic that works reliably across all browsers
  const scrollToBottom = useRef(() => {
    const container = document.querySelector('.messageHistory');
    const historyContainer = document.querySelector('.history-conversationlist');
    
    if (!bottomRef.current) return;
    
    // Force a reflow to ensure all content is rendered
    const forceReflow = () => {
      if (container) {
        void container.offsetHeight; // Force reflow
      }
      if (historyContainer) {
        void historyContainer.offsetHeight; // Force reflow
      }
    };
    
    // Enhanced scroll methods with proper timing
    const performScroll = () => {
      forceReflow();
      
      // Method 1: Aggressive scroll to maximum possible position
      if (container) {
        // Get the actual full height including all content
        const fullHeight = Math.max(
          container.scrollHeight,
          container.offsetHeight,
          container.clientHeight
        );
        container.scrollTop = fullHeight;
      }
      
      if (historyContainer) {
        const fullHeight = Math.max(
          historyContainer.scrollHeight,
          historyContainer.offsetHeight,
          historyContainer.clientHeight
        );
        historyContainer.scrollTop = fullHeight;
      }
      
      // Method 2: ScrollIntoView to the bottom reference
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ 
          behavior: 'instant',
          block: 'end',
          inline: 'nearest'
        });
      }
      
      // Method 3: Final aggressive scroll after scrollIntoView
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        if (historyContainer) {
          historyContainer.scrollTop = historyContainer.scrollHeight;
        }
      }, 50);
    };
    
    // Use RequestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        performScroll();
        
        // Additional attempts for stubborn browsers
        setTimeout(performScroll, 100);
        setTimeout(performScroll, 200);
        
        // Final attempt for Safari/iOS
        if (browserInfo.isSafari || browserInfo.isIOS) {
          setTimeout(performScroll, 400);
          setTimeout(performScroll, 600);
        }
      });
    });
  });

  // Initial scroll after data loads
  useLayoutEffect(() => {
    const chathistory = pathname.includes('chathistory');
    if (chathistory && data && data.length > 0 && !historyLoading) {
      // Scroll immediately when data loads
      const delay = browserInfo.isSafari || browserInfo.isIOS ? 200 : 100;
      setTimeout(() => {
        scrollToBottom.current();
      }, delay);
    }
  }, [pathname, data, historyLoading, browserInfo]);

  // Final scroll when all content including images is loaded
  useLayoutEffect(() => {
    const chathistory = pathname.includes('chathistory');
    if (isContentLoaded && contentReadyToShow && chathistory && data && data.length > 0) {
      // Final positioning when all content is ready
      const delay = browserInfo.isSafari || browserInfo.isIOS ? 500 : 200;
      setTimeout(() => {
        scrollToBottom.current();
        
        // Additional delay to ensure scroll completes before hiding loader
        setTimeout(() => {
          if (safariLoaderMinTime) {
            // Final scroll check right before hiding loader
            scrollToBottom.current();
            
            // Extra delay for complete positioning
            setTimeout(() => {
              setShowInitialLoader(false);
            }, browserInfo.isSafari || browserInfo.isIOS ? 400 : 200);
          }
        }, 200); // Increased delay to ensure scroll completion
      }, delay);
    }
  }, [isContentLoaded, contentReadyToShow, pathname, data, safariLoaderMinTime, browserInfo]);

  // Fallback: Hide loader if minimum time has passed and data is loaded
  useEffect(() => {
    const chathistory = pathname.includes('chathistory');
    if (chathistory && data && data.length > 0 && safariLoaderMinTime && !historyLoading) {
      // If content hasn't loaded images but we have data and minimum time has passed
      const fallbackTimer = setTimeout(() => {
        if (showInitialLoader) {
          setContentReadyToShow(true);
          setIsContentLoaded(true);
          scrollToBottom.current();
          setTimeout(() => setShowInitialLoader(false), 100);
        }
      }, browserInfo.isSafari || browserInfo.isIOS ? 3000 : 2000); // Longer fallback for Safari/iOS
      
      return () => clearTimeout(fallbackTimer);
    }
  }, [pathname, data, safariLoaderMinTime, historyLoading, showInitialLoader, browserInfo]);

  const callUpdateShopClicked = async (params) => {
    if (process.env.REACT_APP_APPLICATION_VERSION === "Microsoft") {
      return
    }
    try {
      await updateShopClicked(params, (response) => { // updating the shopClicked value    
        if (response.status === 200) {
          const conIndex = data.findIndex((element) => element.uid === response.data.data.uid)
          setData(prev => {
            let updatedData = [...prev];
            updatedData[conIndex] = response.data.data;
            return updatedData;
          });
        }
      }, (err) => {
      });
    } catch (err) {
      console.log('getting error in updating the shopClicked Value update', err);
    }
  }

  const updateShopCard = async (convoId, id) => {
    const params = {
      convoId,
      id
    }
    await callUpdateShopClicked(params);
  }

  const handleValueChange = (childValue) => {
    if (childValue.type === "quickshop") { 
      let test = false
    if (childValue.shopClicked) {
      return
    }
    const matchObject = data.find((element) => element.uid === childValue.convoId)
    matchObject && matchObject?.response?.products?.map((items) => {
      if (items.id === childValue.id) {
        if (items.shopClicked) {
          test = true
        }
      }
    })
      const { convoId, id } = childValue
      if(!test){
        updateShopCard(convoId, id);
      }
    }
  };

  //iphone css fix
  useEffect(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);

    let userAgent = window.navigator.userAgent.toLowerCase(),
      macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i,
      windowsPlatforms = /(win32|win64|windows|wince)/i,
      safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (
      macosPlatforms.test(userAgent) ||
      windowsPlatforms.test(userAgent) ||
      (/android/.test(userAgent) && !safari)
    ) {
      setIsScreenResponsive(true);
    }
  }, []);

  useEffect(() => {
    if (window.innerWidth > 576) {
      setIsMobile(false)
    }
    else {
      setIsMobile(true)
    }
  }, [window.innerWidth])

  useEffect(() => {
    const handlePopState = (event) => {
      dispatch(onHistoryClick(true));
    };
    window.addEventListener("popstate", handlePopState);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [pathname]);


  return (
    <div>
      {sessionId ? (
      <div className="d-flex flex-column">
        {/* Use existing loader for chat history route */}
        {showInitialLoader && pathname.includes('chathistory') && (
          <div 
            className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center" 
            style={{ 
              top: 0, 
              left: 0, 
              backgroundColor: browserInfo.isSafari || browserInfo.isIOS ? 'white' : 'rgba(255, 255, 255, 0.95)', 
              zIndex: 9999 
            }}
          >
            <div className="css-history-spinner"></div>
          </div>
        )}
        <div
          className={`homepage ${loading ? "homepageresize" : ""} 
        ${loading && isScreenResponsive
              ? "res_homepageresize"
              : isScreenResponsive
                ? "res_homepage"
                : ""
            }`}
          id="pageContainer"
        >
          <div className="page-container history-container">
            <div className="chatwindow">
              <div className="history-conversationlist chat_options">
                <div
                  className={`container container-fluid min-height px-0 px-md-3 ${isScreenResponsive && "res_min-height"
                    }`}
                >
                  <div 
                    className="messageHistory"
                    style={{
                      // For Safari/iOS, hide content initially until positioned and minimum time passed
                      ...(pathname.includes('chathistory') && (!contentReadyToShow || showInitialLoader) && {
                        opacity: browserInfo.isSafari || browserInfo.isIOS ? 0 : 1,
                        visibility: browserInfo.isSafari || browserInfo.isIOS ? 'hidden' : 'visible',
                        pointerEvents: 'none'
                      })
                    }}
                  >
                    {
                      (data && data.length > 0 && !historyLoading) ? data.map((con, index) => (
                        <div key={"history" + index}>
                          <div className="col-11 userbubble-prompts">
                            <div
                              className={`chat_msg ms-auto me-3 me-sm-1 fadeOutPromts`}
                            >
                              <div className="d-flex align-items-start ps-0">
                                <span className="badge-text">
                                  {con.question}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className={`${((process.env.REACT_APP_NODE_ENV !== "uat" && process.env.REACT_APP_NODE_ENV !== "production")) ? "session-id" : "session-id-none"}`}>
                            Session Id: {sessionId}
                          </div>
                          {(con.response.responseType ||
                            con.response.responseText ||
                            con.response.outfitUrl ||
                            con.response.responseTitle ||
                            con.response.branding ||
                            con.response.products ||
                            con.response.conclusion) && (
                            <>
                              {((con &&
                                con.response?.responseType &&
                                con.response?.responseType === "ProductListing") ||
                                (con &&
                                  con.response?.responseType &&
                                  con.response?.responseType === "ProductDetail")) && (
                                <div>
                                  <div className={`${!isMobile && "d-none"}`}>
                                    {con?.isSingleLook ? (
                                      <img
                                        src={getBestImageUrl(con)}
                                        alt="headerImg"
                                        className={`${
                                          con.response.outfitUrl
                                            ? "outfitHeaderImg"
                                            : "imgHeader"
                                        } animate-header ${
                                          isImageVisible(con.uid, "mobileBgHeader") ? "visible" : "invisible"
                                        }`}
                                        onLoad={() => handleImageLoad(con.uid, "mobileBgHeader")}
                                      />
                                    ) : (
                                      <>
                                        {con.backgroundImage !== "" ? (
                                          <img
                                            src={con.backgroundImage}
                                            alt="headerImg"
                                            className={`imgHeader animate-header ${
                                              isImageVisible(con.uid, "mobileBgHeader")
                                                ? "visible"
                                                : "invisible"
                                            }`}
                                            onLoad={() =>
                                              handleImageLoad(con.uid, "mobileBgHeader")
                                            }
                                          />
                                        ) : (
                                          <img
                                            src={con.desktopbackgroundImage}
                                            alt="headerImg"
                                            className={`imgHeader animate-header ${
                                              isImageVisible(con.uid, "mobileBgHeader")
                                                ? "visible"
                                                : "invisible"
                                            }`}
                                            onLoad={() =>
                                              handleImageLoad(con.uid, "mobileBgHeader")
                                            }
                                          />
                                        )
                                        }
                                      </>
                                    )} 
                                  </div>

                                  <div className={`${isMobile && "d-none"}`}>
                                    {con?.isSingleLook ? (
                                      <img
                                        src={getBestImageUrl(con, true)}
                                        alt="headerImg"
                                        className={`${
                                          con.response.outfitUrl
                                            ? "outfitHeaderImg-desktop"
                                            : "imgHeader"
                                        } ${
                                          isImageVisible(con.uid, "desktopBgHeader") ? "visible" : "invisible"
                                        }`}
                                        onLoad={() =>
                                          handleImageLoad(con.uid, "desktopBgHeader")
                                        }
                                      />
                                    ) : (
                                      con.desktopbackgroundImage && (
                                        <img
                                          src={con.desktopbackgroundImage}
                                          alt="headerImg"
                                          className={`imgHeader animate-header ${
                                            isImageVisible(con.uid, "desktopBgHeader")
                                              ? "visible"
                                              : "invisible"
                                          }`}
                                          onLoad={() =>
                                            handleImageLoad(con.uid, "desktopBgHeader")
                                          }
                                        />
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          {Object.keys(con.response).length > 0 && (
                            <div>
                              {con &&
                                con.response?.responseType &&
                                con.response?.responseType === "TextOnly" && (
                                  <TextOnly
                                    convoId={con.uid}
                                    index={index}
                                    responseType={con.response.responseType}
                                    badIntentResponseCode={con.response.badIntentResponseCode || null}
                                    responseStatusCode={con.response.responseStatusCode}
                                    text={con.response.responseText}
                                    conclusion={con.response.responseConclusion}
                                    delay={typewritterdelay}
                                    isAnswerComplete={props.isAnswerComplete}
                                    stopClick={true}
                                    effects={false}
                                    lookTitle={con.lookTitle}
                                  />
                                )}
                              {con &&
                                con.response?.responseType &&
                                con.response?.responseType ===
                                "BrandingInformation" && (
                                  <BrandingCard
                                    convoId={con.uid}
                                    index={index}
                                    text={con.response.responseText}
                                    branding={con.response.branding}
                                    conclusion={con.response.responseConclusion}
                                    delay={typewritterdelay}
                                    isAnswerComplete={props.isAnswerComplete}
                                    stopClick={true}
                                    effects={false}
                                    lookTitle={con.lookTitle}
                                  />
                                )}
                                {con &&
                                  con.response?.responseType &&
                                  con.response?.responseType === "ProductListing" &&
                                  con.isSingleLook && (
                                    <div
                                      className="single-look-container"
                                      style={{ minHeight: "300px" }}
                                    >
                                      {isImageVisible(
                                        con.uid,
                                        isMobile ? "mobileBgHeader" : "desktopBgHeader"
                                      ) ? (
                                  <SingleLookTemplate
                                    convoId={con.uid}
                                    index={index}
                                    responseTitle={con.response.responseTitle}
                                    text={con.response.responseText}
                                    branding={con.response.branding}
                                    products={con.response.products}
                                    conclusion={con.response.responseConclusion}
                                    delay={typewritterdelay}
                                    isAnswerComplete={true}
                                    stopClick={true}
                                    onValueChange={handleValueChange}
                                    effects={false}
                                    lookTitle={con.lookTitle}
                                    outfitUrl={con.response.outfitUrl}
                                  />
                                ) : (
                                  <div
                                    className="loading-placeholder"
                                    style={{ height: "100%", width: "100%" }}
                                  ></div>
                                )}
                              </div>
                            )}
                            {con &&
                              con.response?.responseType &&
                              con.response?.responseType === "ProductListing" &&
                              !con.isSingleLook && (
                                <div
                                  className="product-listing-container"
                                  style={{ minHeight: "300px" }}
                                >
                                  {isImageVisible(
                                    con.uid,
                                    isMobile ? "mobileBgHeader" : "desktopBgHeader"
                                  ) ? (
                                    <ProductListingGrid
                                      convoId={con.uid}
                                      index={index}
                                      responseTitle={con.response.responseTitle}
                                      text={con.response.responseText}
                                      outfitUrl={
                                        con.response.outfitUrl || con.backgroundImage
                                      }
                                      branding={con.response.branding}
                                      products={con.response.products}
                                      conclusion={con.response.responseConclusion}
                                      delay={typewritterdelay}
                                      isAnswerComplete={props.isAnswerComplete}
                                      stopClick={true}
                                      onValueChange={handleValueChange}
                                      effects={false}
                                      lookTitle={con.lookTitle}
                                    />
                                  ) : (
                                    <div
                                      className="loading-placeholder"
                                      style={{ height: "100%", width: "100%" }}
                                    ></div>
                                  )}
                                </div>
                              )}
                            {con &&
                              con.response?.responseType === "ProductDetail" && (
                                <ProductDetailPage
                                  convoId={con.uid}
                                  index={index}
                                  responseTitle={con.response.responseTitle}
                                  text={con.response.responseText}
                                  branding={con.response.branding}
                                  product={con.response.products}
                                  conclusion={con.response.responseConclusion}
                                  delay={typewritterdelay}
                                  isAnswerComplete={props.isAnswerComplete}
                                  stopClick={true}
                                  onValueChange={handleValueChange}
                                  effects={false}
                                  lookTitle={con.lookTitle}
                                />
                              )}
                            {con &&
                              con.response?.responseType === "ProductCollectionListing" && (
                                <div>
                                  <MultiCatTemplate
                                    convoId={con.uid}
                                    index={index}
                                    responseType={con.response.responseType}
                                    responseTitle={con.response.responseTitle}
                                    text={con.response.responseText}
                                    branding={con.response.branding}
                                    products={con.response.productCollections}
                                    conclusion={con.response.responseConclusion}
                                    delay={typewritterdelay}
                                    isAnswerComplete={props.isAnswerComplete}
                                    stopClick={true}
                                    effects={false}
                                    backgroundImage={con.backgroundImage}
                                    desktopbackgroundImage={con.desktopbackgroundImage}
                                    lookTitle={con.lookTitle}
                                  />
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                  )):(
                    <div className="history_spinner d-flex justify-content-center align-items-center">
                      <div className="css-history-spinner"></div>
                    </div>
                  )}
                  
                    {(data && data.length > 0 && !historyLoading) &&
                      <ChatOperation data={location.state} />
                    }
                    
                    {/* Bottom reference div - positioned after all content for accurate scrolling */}
                    <div 
                      ref={bottomRef} 
                      style={{ 
                        height: "1px", 
                        width: "100%",
                        clear: "both",
                        marginTop: "10px" // Small margin to ensure it's clearly at the bottom
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>) : (
        <div style={{height: "100vh"}} className="pt-5 ms-4">
          <div className="generic-error col-10">
            <div className="generic-error-txt">
              {t("Session Id is required.")}
            </div>
          </div>
        </div>
        )}
    </div>
  );
}

ChatHistory.propTypes = ChatHistoryProps;

export default ChatHistory;
